import RedrawService from 'mithril/api/redraw';
import MStatic from 'mithril/hyperscript';
import { autorun } from 'mobx';
import type { IMithrilMountableComponent } from '../components/mithril.component';
import type { MithrilVNode } from '../mithril';
import { type IkeClientService, Services } from './index';
import { Logger } from './log.service';
import { IkeTrigger } from './util/trigger';

export const m = MStatic;

export interface ComponentElement extends Element {
	$mobxDispose?: (() => void) | null;
}

export class RenderService implements IkeClientService {
	__priority = 10;

	private boundRedraw: RedrawService;

	init() {
		this.boundRedraw = RedrawService(Services.Window.window);
	}

	render(el: ComponentElement, nodes: MithrilVNode | MithrilVNode[]) {
		this.boundRedraw.render(el, nodes);
	}

	mount(el: ComponentElement, component: IMithrilMountableComponent, name: string) {
		if (component == null) {
			if (el.$mobxDispose) {
				el.$mobxDispose();
				el.$mobxDispose = null;
			}
			return;
		}

		const trigger = new IkeTrigger(name);
		let lastRedraw = Date.now();
		const boundRedraw = this.boundRedraw;

		function renderFunction() {
			try {
				const startTime = Date.now();

				// Force watching the trigger,
				// This will allow us to manually trigger redraws when needed.
				trigger.observe();
				const sinceLastRedraw = startTime - lastRedraw;

				// VNodes need to be computed every time otherwise everything inside the component
				// will become un-observed on every redraw.
				const nodes = component.view();
				if (sinceLastRedraw < 16 || trigger.isTriggering) {
					trigger.trigger();
					return;
				}

				// Mithril redraw
				boundRedraw.render(el, nodes);
				lastRedraw = Date.now();

				const duration = lastRedraw - startTime;
				if (duration > 100) {
					Services.Log.Logger.warn({ component: name, duration }, 'Long redraw');
					Services.Analytics.trackEvent(Services.Analytics.EventCategory.Performance, 'draw:slow', name, duration);
				}
			} catch (error) {
				Logger.error(`Failed to render component ${name}`);

				throw error;
			}
		}

		el.$mobxDispose = autorun(renderFunction, { name: `reaction:${name}` });
	}
}
